import { Component, h, Prop, Event, EventEmitter, State, Fragment, Element, Watch } from '@stencil/core';
import '../../../stories/Components/Head/Head';
import { ConversationDetails } from '../../../types/ConversationDetailsTypes';
import { getFormattedTime } from '../../../lib/helpers';
import { conversationStore } from '../../../store/conversation';

@Component({
  tag: 'web-chat-header',
  shadow: true,
  styles: `
    .loading-overlay {
       position: fixed;
       top: 50%;
       left: 50%;
       transform: translate(-50%, -50%);
       padding: 20px;
       border-radius: 5px;
       z-index: 10000;
       background-color: rgba(0, 0, 0, 0.5); 
       height: 100%;
       width: 100%;
       display: flex;
       align-items: center;
       justify-content: center;
    }
  `
})

class WebChatHeader {  
  @Prop() conversationDetails: ConversationDetails;
  @Prop() isTyping: boolean;
  @Prop() onClearConversation: () =>  void;
  @Prop() onConversationCleared: () => void;
  @Prop() headerBgColor: string;
  @Prop() headerUsernameColor: string;
  @Prop() headerSvgColor: string;
  @Prop() headerLastSeenColor: string;
  @Prop() searchedMessageDetails: any
  @Prop() onSearchResultClick: (messageId: string) => void;
  @Prop() chatBodyRef: HTMLWebChatBodyElement;
  @Prop() allAttachments: any;
  @Prop() lastSeen: string;
  @Prop() userMetaData: any;
  @Prop() isLoadingMessage: boolean;
  @Prop() messageAction:object;
  @Prop() hasMoreEligibleMembers: boolean;
  @Prop() typingConversationId: string;
  @Prop() isBlocked: boolean | string = null;
  @Prop() onBackButtonClick: Function;
  @Prop() leofferAccountId: string;
  @Prop() getPaymentsData: Function;
  @Event() searchInputChange: EventEmitter<string>;
  @Event() closeChat: EventEmitter<void>;
  @Event() textClick: EventEmitter<void>;
  @Prop() handleCloseChat: () => void;
  @Prop() onSearchHandler: (searchText: string) => void;
  @Prop() selectedConversationId:string;
  @Element() el: HTMLElement;
  @State() isLoading: boolean = false;
  @State() groupMembers: any[] = [];
  @Prop() eligibleMembers: any[] = [];
  @State() displayTitle: string = '';
  @Prop() typingUserName: string = '';
  
  // Method to clear conversation store
  clearConversationStore() {
    this.displayTitle = '';
    conversationStore.updateConversationDetails({
      conversationTitle: '',
      conversationId: '',
      isGroup: false,
      conversationImageUrl: '',
      opponentDetails: {
        userName: '',
        userProfileImageUrl: ''
      }
    });
  }
  
  componentWillLoad() {
    // Initialize display title immediately
    if (this.conversationDetails) {
      this.displayTitle = this.conversationDetails.isGroup ? this.conversationDetails.conversationTitle : this.conversationDetails.opponentDetails?.userName;
      
      // Initialize store with conversation details
      conversationStore.updateConversationDetails({
        conversationTitle: this.conversationDetails.isGroup ? this.conversationDetails.conversationTitle : this.conversationDetails.opponentDetails?.userName,
        // @ts-ignore 
        conversationId: this.conversationDetails.conversationId,
        isGroup: this.conversationDetails.isGroup,
        conversationImageUrl: this.conversationDetails.isGroup ? this.conversationDetails.conversationImageUrl : this.conversationDetails.opponentDetails?.userProfileImageUrl,
        opponentDetails: this.conversationDetails.opponentDetails
      });
    } else {
      // Clear everything if no conversation details
      this.clearConversationStore();
    }

    // Subscribe to store changes
    conversationStore.onChange('conversationTitle', (value: string) => {
      this.displayTitle = value;
    });
  }

  @Watch('conversationDetails')
  handleConversationDetailsChange(newValue: ConversationDetails) {
    if (newValue) {
      // Clear previous data first
      this.clearConversationStore();
      
      // Update store with new conversation details
      conversationStore.updateConversationDetails({
        conversationTitle: newValue.isGroup ? newValue.conversationTitle : newValue.opponentDetails?.userName,
        // @ts-ignore 
        conversationId: newValue.conversationId,
        isGroup: newValue.isGroup,
        conversationImageUrl: newValue.isGroup ? newValue.conversationImageUrl : newValue.opponentDetails?.userProfileImageUrl,
        opponentDetails: newValue.opponentDetails
      });
      
      // Set display title immediately
      this.displayTitle = newValue.isGroup ? newValue.conversationTitle : newValue.opponentDetails?.userName;
    } else {
      // Clear everything when no conversation is selected
      this.clearConversationStore();
    }
  }

  handleClearChat = () => {
    if ( this.onClearConversation) {
     this.onClearConversation()

     if(this.onConversationCleared) {
      this.onConversationCleared()
     }
    }
  }

  handleSearchInput = (searchText: string) => {
    this.onSearchHandler(searchText)
  }

  handleSearchResultClick = async (messageId: string) => {
    if (this.chatBodyRef) {
      await this.chatBodyRef.scrollToMessage(messageId);
    }
    if (this.onSearchResultClick) {
      this.onSearchResultClick(messageId);
    }
  }

  handleTextClick = () => {
   this.textClick.emit();
  }

  render() {
    const formattedLastSeen = getFormattedTime(this.lastSeen, true);
    
    // Extract nested ternary operations for better readability
    let profileImage = '';
    if (this.conversationDetails?.groupcastId) {
      profileImage = this.conversationDetails?.groupcastImageUrl;
    } else if (this.conversationDetails?.isGroup) {
      profileImage = this.conversationDetails?.conversationImageUrl;
    } else {
      profileImage = this.conversationDetails?.opponentDetails?.userProfileImageUrl;
    }
    
    let displayText = '';
    if (this.conversationDetails?.groupcastTitle) {
      displayText = this.conversationDetails?.groupcastTitle;
    } else if (this.displayTitle) {
      displayText = this.displayTitle;
    } else if (this.conversationDetails?.isGroup) {
      displayText = this.conversationDetails?.conversationTitle;
    } else {
      displayText = this.conversationDetails?.opponentDetails?.userName;
    }
    
    return (
      <Fragment>
      <my-head
        profileImage={profileImage}
        isActionsNeeded={true}
        text={displayText}
        padding="10px"
        isTyping={this.isTyping}
        typingUserName={this.typingUserName}
        lastSeenTextColor={this.headerLastSeenColor}
        backgroundColor={this.headerBgColor}
        textColor={this.headerUsernameColor}
        svgFillColor={this.headerSvgColor}
        onClearChat = {this.handleClearChat}
        onSearchInput={this.handleSearchInput}
        searchedMessageDetails={this.searchedMessageDetails}
        onSearchResultClick={this.handleSearchResultClick}
        conversationDetails={this.conversationDetails}
        onCloseChat={this.handleCloseChat}
        onTextClick={this.handleTextClick}
        lastSeenText={formattedLastSeen}
        allAttachments={this.allAttachments}
        userMetaData={this.userMetaData}
        leofferAccountId={this.leofferAccountId}
        selectedConversationId={this.selectedConversationId}
        isLoadingMessage={this.isLoadingMessage}
        messageAction={this.messageAction}
        eligibleMembers={this.eligibleMembers}
        hasMoreEligibleMembers={this.hasMoreEligibleMembers}
        typingConversationId={this.typingConversationId}
        isBlocked={this.isBlocked}
        onBackButtonClick={this.onBackButtonClick}
        onPaymentClicked={(e) => this.getPaymentsData(e)}
      />

      {this.isLoading && <div class="loading-overlay">
        <spinner-loader height="30px" width="30px" spinTime="1s"  
              spinnerPrimaryColor="var(--bg-primary-color)" 
              spinnerSecondaryColor="var(--text-primary-color)" 
              spinnerBorderWidth="2px">

        </spinner-loader>
      </div>}
      </Fragment>
    );
  }
}

export { WebChatHeader };